import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  AlertTitle,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Key as KeyIcon,
  VpnKey as VpnKeyIcon,
  CheckCircleOutline as CheckIcon,
  Business as BusinessIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import api from '../services/api';
import configurationService from '../services/configurationService';
import { toast } from 'react-toastify';
import { formatarCPFCNPJ, formatarCPFCNPJOnInput } from '../utils/formatToView';

interface LicenseModalProps {
  open: boolean;
  onSuccess?: () => void;
  onClose?: () => void;
  errorDetails?: string;
}

export const LicenseModal: React.FC<LicenseModalProps> = ({
  open,
  onSuccess,
  onClose,
  errorDetails,
}) => {
  const [licenseKey, setLicenseKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Estados para o modal de dados da empresa
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [loadingCompany, setLoadingCompany] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);
  const [provedorNome, setProvedorNome] = useState('');
  const [provedorCnpj, setProvedorCnpj] = useState('');
  const [provedorEndereco, setProvedorEndereco] = useState('');

  const handleOpenCompanyModal = async () => {
    setCompanyModalOpen(true);
    setLoadingCompany(true);
    try {
      const response: any = await api.get('/license/company');
      const data = response?.data?.data || response?.data || {};
      setProvedorNome(data.provedor_nome || '');
      setProvedorCnpj(formatarCPFCNPJ(data.provedor_cnpj || ''));
      setProvedorEndereco(data.provedor_endereco || '');

    } catch (err: any) {
      console.error('Erro ao carregar dados da empresa:', err);
      toast.error('Não foi possível carregar os dados atuais da empresa.');
    } finally {
      setLoadingCompany(false);
    }
  };

  const handleSaveCompany = async () => {
    setSavingCompany(true);
    try {
      await api.put('/license/company', {
        provedor_nome: provedorNome,
        provedor_cnpj: provedorCnpj,
        provedor_endereco: provedorEndereco,
      });

      toast.success('Dados da empresa atualizados com sucesso!');
      setErrorMessage(''); // limpa mensagem de erro de CNPJ anterior para nova tentativa
      setCompanyModalOpen(false);
    } catch (err: any) {
      console.error('Erro ao salvar dados da empresa:', err);
      toast.error(err.response?.data?.error || err.message || 'Erro ao salvar dados da empresa.');
    } finally {
      setSavingCompany(false);

    }
  };

  const handleActivate = async () => {
    if (!licenseKey.trim()) {
      setErrorMessage('Por favor, informe a chave da licença.');
      return;
    }

    try {
      setLoading(true);
      setErrorMessage('');
      setSuccessMessage('');

      const response: any = await api.post('/license/activate', {
        license_key: licenseKey.trim(),
      });

      const data = response && response.data ? response.data : response;

      if (data && (data.success || data.valid)) {
        setSuccessMessage('Licença ativada com sucesso! O sistema está liberado.');
        setTimeout(() => {
          if (onSuccess) {
            onSuccess();
          } else {
            window.location.reload();
          }
        }, 1200);
      } else {
        setErrorMessage(data?.error || data?.message || 'Chave de licença inválida.');
      }
    } catch (err: any) {
      setErrorMessage(
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        'Não foi possível validar a licença com o servidor central.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              bgcolor: 'warning.light',
              color: 'warning.dark',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <VpnKeyIcon />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Ativação de Licença SYSUP
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Insira a chave fornecida para liberar o acesso ao sistema
            </Typography>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2.5 }}>
          {errorDetails && (
            <Alert severity="warning" sx={{ mb: 2.5 }}>
              {errorDetails}
            </Alert>
          )}

          {errorMessage && (
            <Alert
              severity="error"
              sx={{ mb: 2.5 }}
              action={
                errorMessage.toLowerCase().includes('cnpj') ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleOpenCompanyModal}
                    sx={{ textTransform: 'none', fontWeight: 'bold' }}
                  >
                    Ajustar CNPJ
                  </Button>
                ) : undefined
              }
            >
              {errorMessage.toLowerCase().includes('cnpj') && (
                <AlertTitle sx={{ fontWeight: 'bold' }}>CNPJ Divergente</AlertTitle>
              )}
              {errorMessage}
            </Alert>
          )}

          {successMessage && (
            <Alert icon={<CheckIcon fontSize="inherit" />} severity="success" sx={{ mb: 2.5 }}>
              {successMessage}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Para continuar utilizando o sistema, informe a chave da licença gerada no painel administrativo do provedor.
          </Typography>

          <TextField
            autoFocus
            fullWidth
            label="Chave da Licença"
            placeholder="Ex: SYSUP-XXXX-XXXX-XXXX-XXXX"
            value={licenseKey}
            onChange={(e) => setLicenseKey(e.target.value.toUpperCase())}
            disabled={loading || !!successMessage}
            InputProps={{
              startAdornment: <KeyIcon sx={{ color: 'text.secondary', mr: 1 }} />,
            }}
            helperText="Exemplo: SYSUP-ABCD-1234-EFGH-5678"
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<BusinessIcon />}
            onClick={handleOpenCompanyModal}
            disabled={loading}
          >
            Atualizar dados da Empresa
          </Button>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {onClose && (
              <Button onClick={onClose} disabled={loading} color="inherit">
                Fechar
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleActivate}
              disabled={loading || !licenseKey.trim() || !!successMessage}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <VpnKeyIcon />}
              sx={{ minWidth: 150 }}
            >
              {loading ? 'Validando...' : 'Ativar Licença'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Modal para Atualização de Dados da Empresa */}
      <Dialog
        open={companyModalOpen}
        onClose={() => !savingCompany && setCompanyModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: 'primary.light',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <BusinessIcon />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Dados da Empresa / Provedor
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Configurações vinculadas à licença e sistema
            </Typography>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {loadingCompany ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              <TextField
                fullWidth
                label="Nome / Razão Social (provedor_nome)"
                placeholder="Ex: Minha Empresa Telecom LTDA"
                value={provedorNome}
                onChange={(e) => setProvedorNome(e.target.value)}
                disabled={savingCompany}
                size="small"
              />

              <TextField
                fullWidth
                label="CNPJ (provedor_cnpj)"
                placeholder="Ex: 00.000.000/0000-00"
                value={provedorCnpj}
                onChange={(e) => setProvedorCnpj(formatarCPFCNPJOnInput(e.target.value))}
                disabled={savingCompany}
                helperText="O CNPJ deve coincidir com o CNPJ cadastrado para a chave da licença"
                size="small"
              />


              <TextField
                fullWidth
                label="Endereço Completo (provedor_endereco)"
                placeholder="Ex: Rua Exemplo, 123 - Centro"
                value={provedorEndereco}
                onChange={(e) => setProvedorEndereco(e.target.value)}
                disabled={savingCompany}
                multiline
                rows={2}
                size="small"
              />
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCompanyModalOpen(false)}
            disabled={savingCompany}
            color="inherit"
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveCompany}
            disabled={savingCompany || loadingCompany}
            startIcon={savingCompany ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          >
            {savingCompany ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

